import {useEffect, useRef, useState, useCallback} from "react";
import {platformAPI} from "../../../api/axiosConfig";

export type ImportCounts = {
    total: number;
    created: number;
    updated: number;
    archived: number;
    skipped: number;
    failed: number;
};

export type ImportErrorItem = {
    row: number;
    field: string;
    message: string;
};

export type ImportProgressState = {
    status: string;
    processed: number;
    total: number;
    counts: ImportCounts;
    errors: ImportErrorItem[];
    connected: boolean;
};

function wsURL(importID: string): string {
    const base = process.env.REACT_APP_PLATFORM_API ?? "";
    const wsBase = base.replace(/^https/, "wss").replace(/^http/, "ws");
    return `${wsBase}/api/contacts/import/${importID}/ws`;
}

const defaultCounts: ImportCounts = {total: 0, created: 0, updated: 0, archived: 0, skipped: 0, failed: 0};

export function useImportProgress(importID: string | null): ImportProgressState {
    const [status, setStatus] = useState("queued");
    const [processed, setProcessed] = useState(0);
    const [total, setTotal] = useState(0);
    const [counts, setCounts] = useState<ImportCounts>(defaultCounts);
    const [errors, setErrors] = useState<ImportErrorItem[]>([]);
    const [connected, setConnected] = useState(false);

    const wsRef = useRef<WebSocket | null>(null);

    const applySnapshot = useCallback((rec: {status?: string; counts?: ImportCounts; errors?: ImportErrorItem[]}) => {
        if (rec.status) setStatus(rec.status);
        if (rec.counts) {
            setCounts(rec.counts);
            setTotal(rec.counts.total ?? 0);
        }
        if (rec.errors) setErrors(rec.errors);
    }, []);

    const fetchFallback = useCallback(() => {
        if (!importID) return;
        platformAPI
            .get<{status: string; counts: ImportCounts; errors: ImportErrorItem[]}>(`api/contacts/import/${importID}`)
            .then((res) => applySnapshot(res.data))
            .catch(() => {});
    }, [importID, applySnapshot]);

    useEffect(() => {
        if (!importID) return;

        let destroyed = false;

        const socket = new WebSocket(wsURL(importID));
        wsRef.current = socket;

        socket.onopen = () => {
            if (!destroyed) setConnected(true);
        };

        socket.onmessage = (event) => {
            if (destroyed) return;
            try {
                const msg = JSON.parse(event.data);
                if (msg.type === "snapshot" && msg.import) {
                    applySnapshot(msg.import);
                } else if (msg.type === "status") {
                    setStatus(msg.status ?? "processing");
                } else if (msg.type === "progress") {
                    setProcessed(msg.processed ?? 0);
                    setTotal(msg.total ?? 0);
                    if (msg.counts) setCounts(msg.counts);
                } else if (msg.type === "complete") {
                    setStatus(msg.status ?? "complete");
                    if (msg.counts) setCounts(msg.counts);
                    socket.close();
                }
            } catch {
                // ignore malformed messages
            }
        };

        socket.onclose = () => {
            if (destroyed) return;
            setConnected(false);
            // If the socket closed before we received a terminal status,
            // fall back to a REST fetch to get the final state.
            fetchFallback();
        };

        socket.onerror = () => {
            socket.close();
        };

        return () => {
            destroyed = true;
            wsRef.current?.close();
            wsRef.current = null;
        };
    }, [importID, applySnapshot, fetchFallback]);

    return {status, processed, total, counts, errors, connected};
}
